/**
 * Définition des colonnes du tableau de sélection des références (étape 2 booster).
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SlidersHorizontal } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { Badge } from '@ui/badge.tsx';
import { Button } from '@ui/button.tsx';
import { Checkbox } from '@ui/checkbox.tsx';
import type { ReferenceReferenceReadModel } from '@r/api/generated/client.schemas.ts';
import type { BoosterDraftData } from './create-booster-form-schema.ts';
import {
  type BoosterReferenceTierMap,
  isReferenceCoefficientCustomized
} from './booster-reference-tiers.ts';

interface UseBoosterReferencesTableColumnsParams {
  draft: BoosterDraftData;
  selectedReferenceIds: ReadonlySet<number>;
  customTiersByReferenceId: BoosterReferenceTierMap;
  isReferenceSelectable: (referenceId: number) => boolean;
  getConflictHint: (reference: ReferenceReferenceReadModel) => string;
  onToggleReference: (referenceId: number, checked: boolean) => void;
  onConfigureReference: (reference: ReferenceReferenceReadModel) => void;
}

/** Colonnes produit + badge coefficient global/personnalisé + action de configuration. */
export function useBoosterReferencesTableColumns({
  draft,
  selectedReferenceIds,
  customTiersByReferenceId,
  isReferenceSelectable,
  getConflictHint,
  onToggleReference,
  onConfigureReference
}: UseBoosterReferencesTableColumnsParams) {
  const { t } = useTranslation();

  return useMemo<ColumnDef<ReferenceReferenceReadModel>[]>(() => {
    const getConflictCellClass = (referenceId: number) =>
      isReferenceSelectable(referenceId) ? '' : 'text-muted-foreground opacity-60';

    return [
      {
        id: 'select',
        header: ({ table }) => {
          const selectablePageRows = table
            .getRowModel()
            .rows.filter((row) => isReferenceSelectable(row.original.id));
          const allSelected =
            selectablePageRows.length > 0 &&
            selectablePageRows.every((row) => selectedReferenceIds.has(row.original.id));
          const someSelected = selectablePageRows.some((row) =>
            selectedReferenceIds.has(row.original.id)
          );

          return (
            <Checkbox
              checked={allSelected ? true : someSelected ? 'indeterminate' : false}
              disabled={selectablePageRows.length === 0}
              onCheckedChange={(value) => {
                for (const row of selectablePageRows) {
                  onToggleReference(row.original.id, !!value);
                }
              }}
              aria-label={t('boosters.create.references.select.all.aria')}
            />
          );
        },
        cell: ({ row }) => {
          const isSelectable = isReferenceSelectable(row.original.id);

          return (
            <Checkbox
              checked={selectedReferenceIds.has(row.original.id)}
              disabled={!isSelectable}
              title={isSelectable ? undefined : getConflictHint(row.original)}
              onCheckedChange={(value) => onToggleReference(row.original.id, !!value)}
              aria-label={t('boosters.create.references.select.row.aria')}
            />
          );
        },
        enableSorting: false,
        enableHiding: false
      },
      {
        accessorKey: 'brand',
        header: t('label.product.brand'),
        cell: ({ row }) => (
          <span
            className={getConflictCellClass(row.original.id)}
            title={getConflictHint(row.original)}
          >
            {row.original.brand}
          </span>
        )
      },
      {
        accessorKey: 'productLine',
        header: t('label.product.range'),
        cell: ({ row }) => (
          <span
            className={getConflictCellClass(row.original.id)}
            title={getConflictHint(row.original)}
          >
            {row.original.productLine ?? '—'}
          </span>
        )
      },
      {
        accessorKey: 'name',
        header: t('label.product.name'),
        cell: ({ row }) => (
          <span
            className={getConflictCellClass(row.original.id)}
            title={getConflictHint(row.original)}
          >
            {row.original.name}
          </span>
        )
      },
      {
        accessorKey: 'uniqueCode',
        header: t('label.product.reference'),
        cell: ({ row }) => (
          <span
            className={getConflictCellClass(row.original.id)}
            title={getConflictHint(row.original)}
          >
            {row.original.uniqueCode}
          </span>
        )
      },
      {
        accessorKey: 'pointValue',
        header: t('label.product.value'),
        cell: ({ row }) => (
          <span
            className={getConflictCellClass(row.original.id)}
            title={getConflictHint(row.original)}
          >
            {row.original.pointValue}
          </span>
        )
      },
      {
        id: 'coefficientMode',
        header: t('boosters.create.references.coefficient.column'),
        cell: ({ row }) => {
          const isSelected = selectedReferenceIds.has(row.original.id);
          const isCustomized = isReferenceCoefficientCustomized(
            row.original.id,
            customTiersByReferenceId,
            draft.coefficient
          );

          return (
            <Badge variant={isCustomized ? 'default' : 'outline'} className="font-normal">
              {isSelected
                ? isCustomized
                  ? t('boosters.create.references.coefficient.custom')
                  : t('boosters.create.references.coefficient.global', {
                      coefficient: draft.coefficient
                    })
                : '—'}
            </Badge>
          );
        },
        enableSorting: false
      },
      {
        id: 'coefficientActions',
        header: '',
        cell: ({ row }) => {
          const isSelected = selectedReferenceIds.has(row.original.id);
          const isSelectable = isReferenceSelectable(row.original.id);

          if (!isSelected || !isSelectable) {
            return null;
          }

          return (
            <Button
              type="button"
              variant="outline"
              size="icon"
              rounded="full"
              onClick={() => onConfigureReference(row.original)}
              aria-label={t('boosters.create.references.coefficient.configure')}
              title={t('boosters.create.references.coefficient.configure')}
            >
              <SlidersHorizontal className="w-4 h-4" />
            </Button>
          );
        },
        enableSorting: false,
        enableHiding: false
      }
    ];
  }, [
    customTiersByReferenceId,
    draft.coefficient,
    getConflictHint,
    isReferenceSelectable,
    onConfigureReference,
    onToggleReference,
    selectedReferenceIds,
    t
  ]);
}
